import React, { useCallback } from 'react';

import {
	Box,
	Button,
	Checkbox,
	TCell,
	TRow
} from "@launchmetrics/lm-ui";

import { TrashOutline, FileExportOutline, FileTypePptOutline, WorldWwwOutline, FileTypeXlsOutline } from "@launchmetrics/lm-icons";
import { ReportConfigurationModel } from 'store/models/reportConfiguration';
import { setReportById } from "store/article/report/reducers";

import { useStyles as useTableStyles } from '../styles';
import { useStyles } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import moment from 'moment-timezone';
import { UserObject } from 'class/User';
import { State } from 'store/types';

type ReportRowProps = {
    report: ReportConfigurationModel
    selected: boolean,
    onRowSelect: (ev: React.ChangeEvent<HTMLInputElement>, id: number) => void,
    onRowDelete: (id: number) => void
}

const ReportRow = (props: ReportRowProps) => {

	const { report, selected, onRowSelect, onRowDelete } = props;

	const { t } = useTranslation();
	const dispatch = useDispatch();
	const tableStyles = useTableStyles();
	const rowStyles = useStyles({ selected });

	const user: UserObject = useSelector((state: State) => state.app.profile.user) as UserObject;

	const handleSelectRowChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
		onRowSelect(ev, report.id!);
	},[onRowSelect, report.id]);

	const handleRowDelete = useCallback(() => {
		onRowDelete(report.id!);
	},[onRowDelete, report.id]);

	const handleRowExport = useCallback(() => {
		dispatch({ type: 'report/exportReport', payload: report });
	}, [dispatch, report]);

	const reportTypeCell = React.useMemo(() => {
		let icon: React.ReactNode;
		let textKey = 'reports.manager.table.type';
		switch (report.type) {
			case 'powerpoint':
				icon = <FileTypePptOutline />;
				textKey += '.powerpoint';
				break;
			case 'online':
				icon = <WorldWwwOutline />;
				textKey += '.online';
				break;
			case 'excel':
				icon = <FileTypeXlsOutline />;
				textKey += '.excel';
				break;
			default:
				break;
		}
		return <Box styleProps={rowStyles.reportTypeContainer}>{icon} {t(textKey)}</Box>;
	}, [report.type, rowStyles.reportTypeContainer, t]);

	const creationDateCell = React.useMemo(() => {
		const baseDate = moment(report.inserted_at).tz(user.settings.timezone);
		return (
			<Box>
				{baseDate.format('DD/MMM/YYYY')}
				<br />
				{baseDate.format('HH:mm:ss z')}
			</Box>
		);
	}, [report.inserted_at, user.settings.timezone]);

	return (
		<TRow
			id={`report-manager-table-row-${report.id}`}
			styleProps={rowStyles.row}
		>
			<TCell styleProps={tableStyles.tableCells}>
				<Box styleProps={tableStyles.checkBoxContainer}>
					<Checkbox
						checked={selected}
						size="medium"
						indeterminate={false}
						onChange={handleSelectRowChange}
						id={`report-manager-table-row-checkbox-${report.id}`}
					/>
				</Box>
			</TCell>
			<TCell onClick={() => dispatch(setReportById({ id: report.id!, navigate: true }))} styleProps={rowStyles.reportName}>{report.name}</TCell>
			<TCell>{reportTypeCell}</TCell>
			<TCell>{creationDateCell}</TCell>
			<TCell> - </TCell>
			<TCell>
				<Button
					onClick={handleRowExport}
					leftIcon={<FileExportOutline />}
					variant='textGray'
				>
					{t('reports.side_panel.footer.export')}
				</Button>
			</TCell>
			<TCell styleProps={rowStyles.deleteIconContainer}>
				{!selected &&
					<Button variant='textGray' onClick={handleRowDelete}>
						<TrashOutline id={`report-manager-table-row-delete-${report.id}`} styleProps={rowStyles.deleteIcon}/>
					</Button>
				}
			</TCell>
		</TRow>
	);
};

export default ReportRow;
